import { useMemo } from "react";
import {
  AlertTriangle,
  BarChart3,
  CheckCircle2,
  Clock,
  LineChart,
} from "lucide-react";
import { BlockLink } from "@/components/DrilldownLink";
import {
  buildApaarBlockActions,
  buildApaarBlockBacklogList,
  buildApaarBlockComparison,
  buildApaarBlockGaugeStyle,
  buildApaarBlockHeroStats,
  buildApaarBlockInsightCards,
  buildApaarBlockLeaderboard,
  buildApaarBlockPrimaryAction,
  buildApaarBlockRankingTable,
  buildApaarBlockStatusChips,
  buildApaarBlockStatusCounts,
} from "@/utils/apaarDerived";
import "./ApaarBlockPerformanceTab.css";
import { toTitleCase } from "../utils/toTitleCase";

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const INSIGHT_ICONS = {
  green: CheckCircle2,
  red: AlertTriangle,
  orange: Clock,
  "": LineChart,
};

const ACTION_ICONS = {
  red: AlertTriangle,
  orange: Clock,
  green: CheckCircle2,
  "": BarChart3,
};

const ApaarBlockPerformanceTab = ({ overview = {}, blockData = [] }) => {
  const heroStats = useMemo(
    () => buildApaarBlockHeroStats(blockData, overview),
    [blockData, overview]
  );
  const gaugeStyle = useMemo(
    () => buildApaarBlockGaugeStyle(heroStats.districtRate),
    [heroStats.districtRate]
  );
  const statusChips = useMemo(() => buildApaarBlockStatusChips(blockData), [blockData]);
  const primaryAction = useMemo(() => buildApaarBlockPrimaryAction(blockData), [blockData]);
  const insightCards = useMemo(
    () => buildApaarBlockInsightCards(blockData, overview),
    [blockData, overview]
  );
  const leaderboard = useMemo(() => buildApaarBlockLeaderboard(blockData), [blockData]);
  const backlogList = useMemo(() => buildApaarBlockBacklogList(blockData), [blockData]);
  const statusCounts = useMemo(() => buildApaarBlockStatusCounts(blockData), [blockData]);
  const rankingTable = useMemo(() => buildApaarBlockRankingTable(blockData), [blockData]);
  const actions = useMemo(() => buildApaarBlockActions(blockData), [blockData]);
  const comparison = useMemo(
    () => buildApaarBlockComparison(blockData, overview),
    [blockData, overview]
  );

  return (
    <div className="apb-blocks-tab" data-testid="apaar-block-performance">
      <section className="apb-hero-card" id="apb-block-hero">
        <div className="apb-block-hero-layout">
          <div>
            <div className="apb-block-main">
              <div
                className="apb-gauge"
                style={gaugeStyle}
                aria-label={`District APAAR generation rate ${heroStats.districtRate} percent`}
              >
                <div>
                  <strong>{heroStats.districtRate}%</strong>
                  <span>Gen Rate</span>
                </div>
              </div>
              <div className="apb-hero-copy">
                <h2>Block Performance Control Center</h2>
                <p>
                  {heroStats.blockCount} blocks are monitored. The dashboard now highlights where
                  generation is strong, where backlog is concentrated, and which blocks need first
                  follow-up.
                </p>
                <div className="apb-status-row">
                  {statusChips.map((chip) => (
                    <span
                      key={chip.label}
                      className={`apb-status-chip ${chip.className}`.trim()}
                    >
                      {chip.label}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div className="apb-summary-note">
              <div className="apb-icon-box red">
                <AlertTriangle size={22} />
              </div>
              <div>
                <b>Primary action focus</b>
                <span>{primaryAction}</span>
              </div>
            </div>
          </div>

          <div className="apb-metric-grid" aria-label="Block performance KPIs">
            {heroStats.kpis.map((card) => (
              <article key={card.label} className={`apb-metric-card ${card.cardClass}`.trim()}>
                <small>{card.label}</small>
                <strong>{card.value}</strong>
                <span>{card.sub}</span>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="apb-insight-grid" aria-label="Block performance summary cards">
        {insightCards.map((card) => {
          const Icon = INSIGHT_ICONS[card.iconClass] ?? LineChart;
          return (
            <article key={card.id} className="apb-insight-card">
              <div className="apb-head">
                <div className={`apb-icon-box ${card.iconClass}`.trim()}>
                  <Icon size={22} />
                </div>
                <span className={`apb-pill ${card.pillClass}`.trim()}>{card.pill}</span>
              </div>
              <h3>{toTitleCase(card.title)}</h3>
              <strong>{card.value}</strong>
              <p>{card.body}</p>
              <div className="apb-mini-progress">
                <i style={{ width: `${card.progress}%`, background: card.progressColor }} />
              </div>
            </article>
          );
        })}
      </section>

      <div className="apb-section-title" id="apb-analytics">
        <div>
          <h2>Performance Diagnostics</h2>
          <p>
            The chart area is split into a clean leaderboard and a backlog concentration list so
            users can compare rates and action pressure separately.
          </p>
        </div>
        <button
          type="button"
          className="apb-section-action"
          onClick={() => scrollToId("apb-ranking")}
        >
          Open full ranking ↓
        </button>
      </div>

      <section className="apb-analytics-grid">
        <article className="apb-panel">
          <div className="apb-panel-head">
            <div>
              <h3>Block Generation Rate Leaderboard</h3>
              <p>Sorted to show strong blocks first while keeping amber and red status visible.</p>
            </div>
            <span className="apb-pill">Generation %</span>
          </div>
          <div className="apb-bar-list" aria-label="Block generation rate bar chart">
            {leaderboard.map((row) => (
              <div key={row.name} className="apb-hbar-row">
                <div className="apb-hbar-label">{row.name}</div>
                <div className="apb-hbar-track">
                  <div
                    className={`apb-hbar ${row.barClass}`.trim()}
                    style={{ width: `${row.widthPct}%` }}
                  />
                </div>
                <div className="apb-hbar-value">{row.displayRate}</div>
              </div>
            ))}
          </div>
        </article>

        <article className="apb-panel">
          <div className="apb-panel-head">
            <div>
              <h3>Backlog Concentration</h3>
              <p>Priority order by pending load and not-applied volume.</p>
            </div>
            <span className="apb-pill red">Action first</span>
          </div>
          <div className="apb-focus-list" aria-label="Backlog concentration list">
            {backlogList.map((item) => (
              <div key={item.rank} className="apb-focus-item">
                <div className="apb-rank-badge">{item.rank}</div>
                <div>
                  <b>{item.name}</b>
                  <span>{item.sub}</span>
                </div>
                <div className="apb-focus-value">
                  <strong>{item.pending}</strong>
                  <small>Pending</small>
                </div>
              </div>
            ))}
          </div>
        </article>
      </section>

      <div className="apb-section-title" id="apb-ranking">
        <div>
          <h2>Prioritized Block Ranking</h2>
          <p>
            Instead of a dense flat table, rows now carry visual status, action labels, and priority
            badges for faster decision-making.
          </p>
        </div>
        <button
          type="button"
          className="apb-section-action"
          onClick={() => scrollToId("apb-actions")}
        >
          View action queue →
        </button>
      </div>

      <section className="apb-two-column">
        <article className="apb-panel apb-table-card">
          <div className="apb-table-head">
            <div>
              <h3>Block-Wise APAAR Performance</h3>
              <p>Sorted by priority and backlog pressure, with status indicators.</p>
            </div>
            <div className="apb-table-toolbar">
              <span className="apb-pill red">High Priority {statusCounts.high}</span>
              <span className="apb-pill orange">Watch {statusCounts.watch}</span>
              <span className="apb-pill green">On Track {statusCounts.onTrack}</span>
            </div>
          </div>
          <div className="apb-table-wrap">
            <table className="apb-block-table" aria-label="Block-wise APAAR performance table">
              <thead>
                <tr>
                  <th>Rank</th>
                  <th>Block</th>
                  <th>Schools</th>
                  <th>Students</th>
                  <th>Generated</th>
                  <th>Gen %</th>
                  <th>Pending</th>
                  <th>Not Applied</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th>Next Action</th>
                </tr>
              </thead>
              <tbody>
                {rankingTable.map((row) => (
                  <tr key={row.block_name}>
                    <td>{row.rank}</td>
                    <td>
                      <span className="apb-block-name">
                        <i className={row.dotClass} />
                        <BlockLink blockName={row.block_name} className="apb-block-link">
                          {row.displayName}
                        </BlockLink>
                      </span>
                    </td>
                    <td>{row.total_schools}</td>
                    <td>{row.total_students}</td>
                    <td className="apb-num-success">{row.total_generated}</td>
                    <td className="score">{row.generation_rate}</td>
                    <td className="apb-num-warning">{row.pending}</td>
                    <td>{row.total_not_applied}</td>
                    <td>
                      <span className={`apb-priority-pill ${row.priorityClass}`.trim()}>
                        {row.priorityDisplay}
                      </span>
                    </td>
                    <td>
                      <span className={`apb-tag ${row.tagClass}`.trim()}>{row.status}</span>
                    </td>
                    <td>{row.nextAction}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </article>

        <article className="apb-panel" id="apb-actions">
          <div className="apb-panel-head">
            <div>
              <h3>Recommended Action Queue</h3>
              <p>Block-level actions for district review.</p>
            </div>
            <span className="apb-pill red">Priority</span>
          </div>
          <div className="apb-action-card">
            {actions.map((item) => {
              const Icon = ACTION_ICONS[item.iconClass] ?? BarChart3;
              return (
                <div key={item.title} className="apb-action-item">
                  <div className={`apb-icon-box ${item.iconClass}`.trim()}>
                    <Icon size={22} />
                  </div>
                  <div>
                    <h4>{item.title}</h4>
                    <p>{item.body}</p>
                  </div>
                </div>
              );
            })}
            <div className="apb-action-footer">
              <b>Suggested review rhythm</b>
              <span>
                Daily review for high-priority blocks, twice-weekly review for watch blocks, and
                weekly monitoring for on-track blocks.
              </span>
            </div>
          </div>
        </article>
      </section>

      <section className="apb-panel" id="apb-comparison">
        <div className="apb-panel-head">
          <div>
            <h3>Executive Comparison Summary</h3>
            <p>Quick readouts that help the district team discuss progress without scanning the full table.</p>
          </div>
          <span className="apb-pill">Decision support</span>
        </div>
        <div className="apb-comparison-grid">
          {comparison.map((card) => (
            <div key={card.title} className="apb-compare-card">
              <b>{card.title}</b>
              <span>{card.body}</span>
              <div className="apb-compare-row">
                <strong>{card.metric}</strong>
                <span>{card.metricLabel}</span>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default ApaarBlockPerformanceTab;
